import React, { useMemo, useState, useEffect } from 'react';
import LogoImg from '../Assets/LogoImg01.png';
import Maintenance01 from '../Assets/Maintenance01.png';
import Maintenance02 from '../Assets/Maintenance02.png';
import Maintenance03 from '../Assets/Maintenance03.png';

const maintenanceImages = [Maintenance01, Maintenance02, Maintenance03];

const STEPS = [
    { label: 'Preparing maintenance', threshold: 0 },
    { label: 'System upgrade in progress', threshold: 25 },
    { label: 'Verifying services', threshold: 60 },
    { label: 'Almost done, coming back online', threshold: 85 },
];

const toDate = (utcStr) => {
    if (!utcStr) return null;
    const s = utcStr.includes('T') && !utcStr.includes('Z') && !utcStr.includes('+')
        ? utcStr + 'Z' : utcStr;
    return new Date(s);
};

const formatTime = (utcStr) => {
    const date = toDate(utcStr);
    if (!date) return null;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toLocaleString(undefined, {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }) + ` (${tz})`;
};

const formatShortTime = (utcStr) => {
    const date = toDate(utcStr);
    if (!date) return '';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toLocaleString(undefined, {
        timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
    });
};

const MaintenancePage = ({ message, recoveryTime, startTime }) => {
    const randomImage = useMemo(() => {
        return maintenanceImages[Math.floor(Math.random() * maintenanceImages.length)];
    }, []);

    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    // 計算 progress
    const progress = useMemo(() => {
        const start = toDate(startTime);
        const end = toDate(recoveryTime);
        if (!start || !end) return 0;
        const total = end.getTime() - start.getTime();
        const elapsed = now - start.getTime();
        if (total <= 0) return 0;
        return Math.min(99, Math.max(0, Math.round((elapsed / total) * 100)));
    }, [startTime, recoveryTime, now]);

    // 計算每個 step 的時間點
    const stepsWithTime = useMemo(() => {
        const start = toDate(startTime);
        const end = toDate(recoveryTime);
        return STEPS.map((step, i) => {
            let stepTime = '';
            if (start && end) {
                const total = end.getTime() - start.getTime();
                const stepMs = start.getTime() + (total * step.threshold / 100);
                stepTime = formatShortTime(new Date(stepMs).toISOString());
            }
            let state = 'todo';
            if (progress >= (STEPS[i + 1]?.threshold ?? 100)) state = 'done';
            else if (progress >= step.threshold) state = 'active';
            return { ...step, time: stepTime, state };
        });
    }, [startTime, recoveryTime, progress]);

    const etaShort = formatShortTime(recoveryTime);
    const etaFull = formatTime(recoveryTime);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, #eef1f8 0%, #f7eef5 60%, #fcecf2 100%)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}>
            <div style={{
                width: '100%',
                maxWidth: '720px',
                background: '#fff',
                borderRadius: '20px',
                boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 640 ? '1fr' : '1.2fr 1fr',
                overflow: 'hidden',
            }}>
                {/* ── Left ── */}
                <div style={{ padding: '44px 40px' }}>
                    <img src={LogoImg} alt="KozyGuru" style={{ height: '46px' }} />

                    <h1 style={{
                        fontSize: '24px', fontWeight: '800',
                        color: '#1a1a2e', margin: '24px 0 10px',
                    }}>
                        System Maintenance
                    </h1>
                    <p style={{
                        fontSize: '13.5px', color: '#667085',
                        lineHeight: '1.65', marginBottom: '22px',
                    }}>
                        {message || "We're upgrading our systems to serve you better. Follow the live progress on the right."}
                    </p>

                    {/* Progress bar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
                        <span style={{ color: '#1aada8' }}>{progress}% complete</span>
                        {etaShort && <span style={{ color: '#98a2b3' }}>ETA {etaShort}</span>}
                    </div>
                    <div style={{
                        height: '8px', background: '#f0f2f5',
                        borderRadius: '99px', overflow: 'hidden', marginBottom: '24px',
                    }}>
                        <div style={{
                            height: '100%',
                            width: `${progress}%`,
                            background: 'linear-gradient(90deg, #3ecfca, #6fdbd7)',
                            borderRadius: '99px',
                            transition: 'width 0.6s ease',
                        }} />
                    </div>

                    {/* Callout */}
                    <div style={{
                        background: '#f9fafb', border: '1px solid #f0f0f0',
                        borderRadius: '12px', padding: '12px 16px',
                        fontSize: '12.5px', color: '#667085', lineHeight: '1.6',
                    }}>
                        💡 Your reservations and data are{' '}
                        <strong style={{ color: '#1a1a2e' }}>not affected</strong>.
                        All bookings continue to sync automatically once we're back.
                    </div>

                    {etaFull && (
                        <div style={{ marginTop: '14px', fontSize: '12px', color: '#98a2b3' }}>
                            Expected recovery: {etaFull}
                        </div>
                    )}

                    <div style={{ marginTop: '22px', fontSize: '12.5px', color: '#98a2b3' }}>
                        Urgent?{' '}
                        <a href="mailto:info@kozyguru.com"
                            style={{ color: '#3ecfca', fontWeight: '600', textDecoration: 'none' }}>
                            info@kozyguru.com
                        </a>
                    </div>
                </div>

                {/* ── Right ── */}
                <div style={{
                    background: '#f7fbfb',
                    borderLeft: window.innerWidth <= 640 ? 'none' : '1px solid #eef2f2',
                    borderTop: window.innerWidth <= 640 ? '1px solid #eef2f2' : 'none',
                    padding: window.innerWidth <= 640 ? '32px 24px' : '44px 32px',
                }}>
                    <img
                        src={randomImage}
                        alt="Maintenance"
                        style={{ height: '96px', display: 'block', margin: '0 auto 24px' }}
                    />

                    <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {stepsWithTime.map((step, i) => (
                            <li key={step.label} style={{ display: 'flex', gap: '12px' }}>
                                {/* Rail */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                    <div style={{
                                        width: '24px', height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '12px', fontWeight: '700',
                                        background: step.state === 'done' ? '#3ecfca' : step.state === 'active' ? '#fff' : '#eef2f2',
                                        color: step.state === 'done' ? '#fff' : step.state === 'active' ? '#3ecfca' : '#b6c2c1',
                                        border: step.state === 'active' ? '2px solid #3ecfca' : 'none',
                                    }}>
                                        {step.state === 'done' ? '✓' : i + 1}
                                    </div>
                                    {i < stepsWithTime.length - 1 && (
                                        <div style={{
                                            width: '2px', flex: 1, minHeight: '20px',
                                            background: step.state === 'done' ? '#3ecfca' : '#e3e9e9',
                                        }} />
                                    )}
                                </div>
                                {/* Body */}
                                <div style={{ padding: '2px 0 18px' }}>
                                    <div style={{
                                        fontSize: '13.5px',
                                        fontWeight: step.state === 'active' ? '700' : '600',
                                        color: step.state === 'todo' ? '#98a2b3' : '#1a1a2e',
                                    }}>
                                        {step.label}
                                    </div>
                                    {step.time && (
                                        <div style={{ fontSize: '11.5px', color: '#98a2b3', marginTop: '1px' }}>
                                            {step.state === 'todo' ? `~${step.time}` : step.time}
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </div>
    );
};

export default MaintenancePage;